import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { ArrowUturnLeftIcon, ArrowsRightLeftIcon, CheckCircleIcon, SparklesIcon, StarIcon, UserGroupIcon, ClipboardDocumentListIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';

const TryoutAssignment = () => {
  const { user, isCoach } = useAuth();
  const authToken = user?.token || localStorage.getItem('token');
  const [eventId, setEventId] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [registrants, setRegistrants] = useState([]);
  const [teams, setTeams] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [selectedAgeGroupKey, setSelectedAgeGroupKey] = useState('');
  const [selectedAgeGroupLabel, setSelectedAgeGroupLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [teamAssignments, setTeamAssignments] = useState({}); // { teamId: [{ userId, name, ... }] }
  const [activeTab, setActiveTab] = useState('assignment'); // 'assignment' | 'evaluators' | 'scores'
  const [evaluators, setEvaluators] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [aggregated, setAggregated] = useState({}); // { [registrantId]: { overall, categories, evaluatorCount } }
  const [userIsManager, setUserIsManager] = useState(false);
  const [userIsEvaluator, setUserIsEvaluator] = useState(false);
  const [selectedEvaluatorIds, setSelectedEvaluatorIds] = useState(new Set());
  const [savingEvaluators, setSavingEvaluators] = useState(false);
  const [editingNumber, setEditingNumber] = useState({}); // { [registrantId]: draftValue }
  const [draftStatus, setDraftStatus] = useState('draft');
  const [draftFinalizedAt, setDraftFinalizedAt] = useState(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [randomizingHatPicks, setRandomizingHatPicks] = useState(false);
  const [finalizingDraft, setFinalizingDraft] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const eId = parseInt(params.get('eventId'), 10);
    setEventId(eId);
  }, []);

  useEffect(() => {
    if (!eventId || !authToken) return;
    fetchData();
  }, [eventId, selectedAgeGroupKey, authToken]);

  const selectedAgeGroup = useMemo(() => {
    if (!selectedAgeGroupKey) {
      return { id: null, name: '' };
    }
    if (selectedAgeGroupKey.startsWith('id:')) {
      const id = parseInt(selectedAgeGroupKey.slice(3), 10);
      const found = ageGroups.find(ag => ag.id === id);
      return { id, name: found?.name || '' };
    }
    if (selectedAgeGroupKey.startsWith('name:')) {
      const name = selectedAgeGroupKey.slice(5);
      const found = ageGroups.find(ag => ag.name === name);
      return { id: found?.id || null, name };
    }
    return { id: null, name: '' };
  }, [selectedAgeGroupKey, ageGroups]);

  const sortedRegistrants = useMemo(() => {
    const withScores = (registrants).map(r => ({
      ...r,
      _avgScore: aggregated[r.registrantId]?.overall ?? null
    }));
    return withScores.sort((a, b) => {
      if (a._avgScore == null && b._avgScore == null) return 0;
      if (a._avgScore == null) return 1;
      if (b._avgScore == null) return -1;
      return b._avgScore - a._avgScore;
    });
  }, [registrants, aggregated]);

  const ageFilteredRegistrants = useMemo(() => {
    if (!selectedAgeGroupKey) return sortedRegistrants;

    const targetId = selectedAgeGroup.id;
    const targetName = selectedAgeGroup.name?.toLowerCase();

    return sortedRegistrants.filter(reg => {
      const assigned = reg.assignedAgeGroup;
      if (!assigned) return false;

      if (targetId && assigned.id) {
        return assigned.id === targetId;
      }
      if (targetName && assigned.name) {
        return assigned.name.toLowerCase() === targetName;
      }
      return false;
    });
  }, [sortedRegistrants, selectedAgeGroupKey, selectedAgeGroup]);

  const filteredRegistrants = useMemo(
    () => ageFilteredRegistrants.filter(reg => !reg.draftTeamId && !reg.isHatPick),
    [ageFilteredRegistrants]
  );

  const hatPickRegistrants = useMemo(
    () => ageFilteredRegistrants.filter(reg => reg.isHatPick && !reg.draftTeamId),
    [ageFilteredRegistrants]
  );

  const canFinalizeDraft = ['org_admin', 'admin', 'app_manager'].includes(user?.role);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // First, fetch event details to check if it's organization-scoped
      const eventRes = await axios.get(`/api/events/${eventId}`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      });
      if (!eventRes.data) {
        throw new Error('No event returned from API');
      }
      const event = eventRes.data;
      if (!event.organization_id) {
        try {
          const assignmentRes = await axios.get(`/api/events/assignment/${eventId}`, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
          });

          if (assignmentRes.data?.event) {
            event.organization_id = assignmentRes.data.event.organization_id || event.organization_id;
            event.age_group_id = assignmentRes.data.event.age_group_id || event.age_group_id;
          } else if (assignmentRes.data?.organization_id) {
            event.organization_id = assignmentRes.data.organization_id;
          }
        } catch (assignmentError) {
          console.warn('Fallback event assignment load failed:', assignmentError);
        }
      }

      setEventData(event);

      const ageGroupParam = (() => {
        if (!selectedAgeGroupKey) return undefined;
        if (selectedAgeGroupKey.startsWith('id:')) {
          return selectedAgeGroupKey.slice(3);
        }
        if (selectedAgeGroupKey.startsWith('name:')) {
          return selectedAgeGroupKey.slice(5);
        }
        return undefined;
      })();

      // Fetch registrants (show all statuses, not just 'paid')
      // This allows viewing registrants with incomplete payments
      const regRes = await axios.get(`/api/events/${eventId}/registrants`, {
        params: { ageGroup: ageGroupParam }, // Removed status: 'paid' filter
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      });
      const draftRes = await axios.get(`/api/events/${eventId}/draft`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      });
      const placements = new Map(
        (draftRes.data?.placements || []).map(placement => [placement.registrantId, placement])
      );
      setRegistrants((regRes.data.registrants || []).map(registrant => ({
        ...registrant,
        ...(placements.get(registrant.registrantId) || {})
      })));
      setDraftStatus(draftRes.data?.status || 'draft');
      setDraftFinalizedAt(draftRes.data?.finalizedAt || null);

      // Fetch age groups FIRST (needed for team filtering)
      const ageGroupParams = {};
      if (event.organization_id) {
        ageGroupParams.organizationId = event.organization_id;
      }

      const groupsRes = await axios.get('/api/age-groups', {
        params: ageGroupParams,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      });

      const rawGroups = Array.isArray(groupsRes.data)
        ? groupsRes.data
        : groupsRes.data?.ageGroups || [];

      const fetchedAgeGroups = rawGroups
        .map(group => {
          if (!group) return null;

          const id = group.id ?? group.age_group_id ?? null;
          const name = group.name || group.ageGroup || '';
          const organizationId = group.organization_id ?? group.organizationId ?? null;
          const isActive = group.is_active ?? group.isActive ?? true;
          const source = group.source || 'configured';

          if (!name) return null;

          return { id, name, organization_id: organizationId, is_active: isActive, source };
        })
        .filter(Boolean)
        .filter(group => group.is_active !== false);

      setAgeGroups(fetchedAgeGroups);
      if (selectedAgeGroupKey) {
        const exists = fetchedAgeGroups.some(ag => {
          if (selectedAgeGroupKey.startsWith('id:')) {
            return ag.id && `id:${ag.id}` === selectedAgeGroupKey;
          }
          if (selectedAgeGroupKey.startsWith('name:')) {
            return `name:${ag.name}` === selectedAgeGroupKey;
          }
          return false;
        });
        if (!exists) {
          setSelectedAgeGroupKey('');
          setSelectedAgeGroupLabel('');
        }
      }

      // Fetch teams based on tryout type:
      // - If organization-scoped (open tryout): show all teams in org that match event's age group
      // - If open tryout without org (organization_id = null but has age_group_id): show all teams matching age group
      // - If team-specific: only show the event's team
      let teamsPayload = [];
      
      console.log(`[TryoutAssignment] Event data:`, {
        id: event.id,
        organization_id: event.organization_id,
        team_id: event.team_id,
        age_group_id: event.age_group_id,
        ageGroup: event.ageGroup,
        type: event.type
      });
      
      // Check if this is an open tryout (has age_group_id and type is 'tryout')
      // If it's a tryout with an age group, show all teams matching that age group
      // (regardless of whether it has organization_id or team_id)
      const isOpenTryout = event.type === 'tryout' && (event.age_group_id || event.ageGroup);
      
      console.log(`[TryoutAssignment] Tryout detection:`, {
        type: event.type,
        age_group_id: event.age_group_id,
        ageGroup: event.ageGroup,
        isOpenTryout,
        organization_id: event.organization_id,
        team_id: event.team_id
      });
      
      if (isOpenTryout || event.organization_id) {
        // Open tryout OR organization-scoped event: get all teams matching age group
        // Filter by event's age_group_id if available, or use selected age group
        const eventAgeGroupId = event.age_group_id || event.ageGroup?.id;
        const eventAgeGroupName = event.ageGroup?.name || (typeof event.ageGroup === 'string' ? event.ageGroup : null);
        const ageGroupToUse = selectedAgeGroupKey ? selectedAgeGroup.id : (eventAgeGroupId || null);
        const ageGroupNameToUse = selectedAgeGroupKey ? selectedAgeGroup.name : (eventAgeGroupName || null);
        
        console.log(`[TryoutAssignment] ${isOpenTryout ? 'Open tryout' : 'Organization-scoped'} - age group filter:`, {
          eventAgeGroupId,
          eventAgeGroupName,
          selectedAgeGroupKey,
          ageGroupToUse,
          ageGroupNameToUse,
          organization_id: event.organization_id
        });
        
        const teamParams = {};
        // Only include organizationId if it exists (null/undefined means show all teams)
        if (event.organization_id) {
          teamParams.organizationId = event.organization_id;
        }
        
        // Send age group by ID when we have it so server filters by age_group_id (reliable for 14U (Global) etc.)
        if (ageGroupToUse != null && ageGroupToUse !== '') {
          teamParams.ageGroupId = ageGroupToUse;
        }
        // Also send name for server fallback / display; server prefers ageGroupId for filtering
        if (ageGroupNameToUse) {
          teamParams.ageGroup = ageGroupNameToUse;
        } else if (ageGroupToUse && !teamParams.ageGroup) {
          const eventAgeGroupFromList = fetchedAgeGroups.find(ag => ag.id === ageGroupToUse);
          if (eventAgeGroupFromList) {
            teamParams.ageGroup = eventAgeGroupFromList.name;
          }
        }

        try {
          const primaryRes = await axios.get('/api/events/teams-by-age-group', {
            params: teamParams,
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
          });
          teamsPayload = primaryRes.data?.teams || primaryRes.data || [];
          console.log(`[TryoutAssignment] Found ${teamsPayload.length} teams ${event.organization_id ? `for org ${event.organization_id}` : '(all teams)'} with age group filter:`, teamsPayload.map(t => ({ id: t.id, name: t.name, ageGroup: t.ageGroup, ageGroupId: t.ageGroupId })));

          // If primary returned 0 teams but we have an age group filter, try fallback: fetch all org teams then filter by age group client-side
          if (teamsPayload.length === 0 && event.organization_id && (ageGroupToUse != null || ageGroupNameToUse)) {
            try {
              const fallbackRes = await axios.get('/api/teams', {
                params: { organizationId: event.organization_id },
                headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
              });
              const allOrgTeams = Array.isArray(fallbackRes.data) ? fallbackRes.data : fallbackRes.data?.teams || [];
              const targetId = ageGroupToUse != null ? Number(ageGroupToUse) : null;
              const targetNameLower = ageGroupNameToUse ? ageGroupNameToUse.toLowerCase() : null;
              teamsPayload = allOrgTeams.filter(team => {
                const teamAgeGroupId = team.ageGroupId ?? team.age_group_id ?? team.AgeGroup?.id;
                const teamAgeGroupName = (team.ageGroup || team.ageGroupName || team.AgeGroup?.name || '').toLowerCase();
                if (targetId != null && teamAgeGroupId != null && teamAgeGroupId === targetId) return true;
                if (targetNameLower && teamAgeGroupName && (teamAgeGroupName === targetNameLower || teamAgeGroupName.startsWith(targetNameLower))) return true;
                return false;
              });
              console.log(`[TryoutAssignment] Fallback (0 from primary): got ${allOrgTeams.length} org teams, ${teamsPayload.length} match age group id=${ageGroupToUse} name=${ageGroupNameToUse}`);
            } catch (fallbackErr) {
              console.warn('[TryoutAssignment] Fallback team fetch failed:', fallbackErr);
            }
          }
        } catch (primaryError) {
          console.error('Primary team fetch failed, attempting fallback:', primaryError);
          const fallbackParams = {};
          if (event.organization_id) {
            fallbackParams.organizationId = event.organization_id;
          }
          try {
            const fallbackRes = await axios.get('/api/teams', {
              params: fallbackParams,
              headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
            });
            teamsPayload = Array.isArray(fallbackRes.data)
              ? fallbackRes.data
              : fallbackRes.data?.teams || [];
            
            console.log(`[TryoutAssignment] Fallback found ${teamsPayload.length} teams, filtering by age group...`);
            
            const targetId = ageGroupToUse != null ? Number(ageGroupToUse) : eventAgeGroupId;
            const targetNameLower = (ageGroupNameToUse || eventAgeGroupName || '').toLowerCase();
            if ((targetId != null || targetNameLower) && teamsPayload.length > 0) {
              teamsPayload = teamsPayload.filter(team => {
                const teamAgeGroupId = team.ageGroupId || team.age_group_id || team.AgeGroup?.id;
                const teamAgeGroupName = (team.ageGroup || team.ageGroupName || team.AgeGroup?.name || '').toLowerCase();
                if (targetId != null && teamAgeGroupId != null && teamAgeGroupId === targetId) return true;
                if (targetNameLower && teamAgeGroupName && (teamAgeGroupName === targetNameLower || teamAgeGroupName.startsWith(targetNameLower))) return true;
                return false;
              });
              console.log(`[TryoutAssignment] After age group filter: ${teamsPayload.length} teams`);
            }
          } catch (fallbackError) {
            console.error('Fallback team fetch also failed:', fallbackError);
            teamsPayload = [];
          }
        }
      } else if (event.team_id) {
        // Team-specific event (not a tryout): only show the event's team
        try {
          const teamRes = await axios.get(`/api/teams/${event.team_id}`, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
          });
          console.log(`[TryoutAssignment] Team API response:`, teamRes.data);
          if (teamRes.data) {
            teamsPayload = [teamRes.data];
            console.log(`[TryoutAssignment] Found team-specific team: ${teamRes.data.name || 'name missing'}`);
          } else {
            console.warn(`[TryoutAssignment] Team API returned no data for team_id ${event.team_id}`);
          }
        } catch (teamError) {
          console.error('Failed to fetch event team:', teamError);
          console.error('Team error details:', teamError.response?.data || teamError.message);
          teamsPayload = [];
        }
      } else {
        console.warn(`[TryoutAssignment] Event has no organization_id, team_id, or age group - cannot determine teams to show`);
      }

      const normalizedTeams = teamsPayload.map(team => ({
        id: team.id,
        name: team.name,
        season: team.season,
        ageGroup: team.ageGroup || team.ageGroupName || team.AgeGroup?.name || '',
        ageGroupId: team.ageGroupId || team.age_group_id || team.AgeGroup?.id || null
      }));

      console.log(`[TryoutAssignment] Normalized teams:`, normalizedTeams.map(t => ({ name: t.name, ageGroup: t.ageGroup, ageGroupId: t.ageGroupId })));

      // For organization-scoped tryouts, filter by selected age group if one is selected
      // For team-specific tryouts, show the team regardless of age group filter
      const filteredTeams = event.organization_id && selectedAgeGroupKey
        ? normalizedTeams.filter(team => {
            if (selectedAgeGroup.id && team.ageGroupId) {
              return team.ageGroupId === selectedAgeGroup.id;
            }
            if (selectedAgeGroup.name && team.ageGroup) {
              return team.ageGroup.toLowerCase() === selectedAgeGroup.name.toLowerCase();
            }
            return false;
          })
        : normalizedTeams;

      console.log(`[TryoutAssignment] Final filtered teams (${filteredTeams.length}):`, filteredTeams.map(t => t.name));
      setTeams(filteredTeams);
      
      // Fetch team memberships to show already assigned players
      // This ensures we show players who were assigned before this session
      const teamIds = filteredTeams.map(t => t.id);
      if (teamIds.length > 0) {
        try {
          // Fetch team members for each team
          const memberPromises = teamIds.map(async (teamId) => {
            try {
              const teamRes = await axios.get(`/api/teams/${teamId}`, {
                headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
              });
              return { teamId, members: teamRes.data?.members || [] };
            } catch (err) {
              console.warn(`Could not fetch members for team ${teamId}:`, err);
              return { teamId, members: [] };
            }
          });
          
          const memberResults = await Promise.all(memberPromises);
          const assignments = {};
          memberResults.forEach(({ teamId, members }) => {
            const players = members
              .filter(m => m.relationshipType === 'player')
              .map(m => ({
                userId: m.id,
                name: m.name || 'Unknown',
                email: m.email || '',
                isGuest: false
              }));
            if (players.length > 0) {
              assignments[teamId] = players;
            }
          });
          setTeamAssignments(assignments);
        } catch (membersError) {
          console.warn('Could not fetch team members:', membersError);
          // Continue without pre-populating assignments
        }
      }
      // Fetch scores (aggregated) and evaluators for this tryout
      const authHeaders = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      try {
        const scoresRes = await axios.get(`/api/tryout-scoring/${eventId}/scores`, { headers: authHeaders });
        setAggregated(scoresRes.data?.aggregated || {});
        const manager = scoresRes.data?.isManager ?? false;
        setUserIsManager(manager);
        setUserIsEvaluator(!manager && scoresRes.data?.scores !== undefined);

        if (manager) {
          const evalRes = await axios.get(`/api/tryout-scoring/${eventId}/evaluators`, { headers: authHeaders });
          setEvaluators(evalRes.data?.evaluators || []);
          setOrgMembers(evalRes.data?.orgMembers || []);
        }
      } catch (scoreErr) {
        // User is not a manager or evaluator — scoring data not shown
        if (scoreErr.response?.status !== 403) {
          console.warn('Score fetch error:', scoreErr.message);
        }
      }
    } catch (err) {
      console.error('Failed to fetch tryout data', err);
      toast.error('Failed to load registrants/teams');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTryoutNumber = async (registrantId, value) => {
    const parsed = value === '' ? null : parseInt(value, 10);
    if (value !== '' && (isNaN(parsed) || parsed < 1)) {
      toast.error('Tryout number must be a positive integer');
      return;
    }
    try {
      await axios.patch(
        `/api/events/${eventId}/registrants/${registrantId}/tryout-number`,
        { tryoutNumber: parsed },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setRegistrants((prev) =>
        prev.map((r) => r.registrantId === registrantId ? { ...r, tryoutNumber: parsed } : r)
      );
      setEditingNumber((prev) => { const next = { ...prev }; delete next[registrantId]; return next; });
      toast.success('Number saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save number');
    }
  };

  const saveDraftPlacement = async (registrant, teamId, isHatPick = registrant.isHatPick) => {
    if (!registrant || draftStatus === 'finalized') return;
    const payload = {
      registrantId: registrant.registrantId,
      invoiceId: registrant.invoiceId,
      userId: registrant.player?.id || registrant.user?.id,
      teamId,
      isHatPick
    };
    try {
      setDraftSaving(true);
      const response = await axios.post(`/api/events/${eventId}/draft/place`, payload, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const updated = response.data;
      setRegistrants(previous => previous.map(item => {
        const isSame = item.registrantId
          ? item.registrantId === updated.registrantId
          : item.invoiceId === registrant.invoiceId;
        return isSame ? {
          ...item,
          registrantId: updated.registrantId,
          draftTeamId: updated.draftTeamId,
          isHatPick: updated.isHatPick
        } : item;
      }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update draft roster');
    } finally {
      setDraftSaving(false);
    }
  };

  const handleRandomizeHatPicks = async () => {
    if (!hatPickRegistrants.length || !teams.length) return;
    const visibleAgeGroups = new Set(
      teams.map(team => team.ageGroupId || team.ageGroup).filter(Boolean)
    );
    if (visibleAgeGroups.size > 1) {
      toast.error('Select one age group before randomizing hat picks');
      return;
    }
    try {
      setRandomizingHatPicks(true);
      const response = await axios.post(`/api/events/${eventId}/draft/randomize-hat-picks`, {
        registrantIds: hatPickRegistrants.map(registrant => registrant.registrantId),
        teamIds: teams.map(team => team.id)
      }, { headers: { Authorization: `Bearer ${authToken}` } });
      const placements = new Map(response.data.placements.map(placement => [placement.registrantId, placement]));
      setRegistrants(previous => previous.map(registrant => (
        placements.has(registrant.registrantId)
          ? { ...registrant, ...placements.get(registrant.registrantId) }
          : registrant
      )));
      toast.success(`${response.data.placements.length} hat pick${response.data.placements.length === 1 ? '' : 's'} randomized`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to randomize hat picks');
    } finally {
      setRandomizingHatPicks(false);
    }
  };

  const handleFinalizeDraft = async () => {
    const unplacedCount = registrants.filter(registrant => !registrant.draftTeamId).length;
    if (unplacedCount) {
      toast.error(`Place all ${unplacedCount} remaining player${unplacedCount === 1 ? '' : 's'} before finalizing`);
      return;
    }
    if (!window.confirm('Finalize these rosters? This creates team memberships, program fees, and sends parent welcome emails.')) return;
    try {
      setFinalizingDraft(true);
      const response = await axios.post(`/api/events/${eventId}/draft/finalize`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      toast.success(`${response.data.assigned} players finalized and parent welcomes started`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to finalize draft');
    } finally {
      setFinalizingDraft(false);
    }
  };

  const onDragStart = (e, registrant) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      registrantId: registrant.registrantId,
      invoiceId: registrant.invoiceId
    }));
  };

  const onDropTeam = async (e, teamId) => {
    e.preventDefault();
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      const registrant = registrants.find(item => (
        dragData.registrantId
          ? item.registrantId === dragData.registrantId
          : item.invoiceId === dragData.invoiceId
      ));
      if (registrant) await saveDraftPlacement(registrant, teamId, registrant.isHatPick);
    } catch {
      toast.error('Could not read the dragged player');
    }
  };

  const onDragOver = (e) => e.preventDefault();

  if (!isCoach) {
    return (
      <div className="bg-white rounded-lg shadow p-6">Coach access required.</div>
    );
  }

  const ScoreBadge = ({ registrantId }) => {
    const agg = aggregated[registrantId];
    if (!agg || agg.overall == null) return null;
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
        <StarSolid className="h-3 w-3 text-amber-400" />
        {agg.overall.toFixed(1)}
        <span className="font-normal text-amber-500">({agg.evaluatorCount})</span>
      </span>
    );
  };

  const handleAddEvaluators = async () => {
    if (selectedEvaluatorIds.size === 0) return;
    setSavingEvaluators(true);
    try {
      await axios.post(
        `/api/tryout-scoring/${eventId}/evaluators`,
        { userIds: [...selectedEvaluatorIds] },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setSelectedEvaluatorIds(new Set());
      const evalRes = await axios.get(`/api/tryout-scoring/${eventId}/evaluators`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setEvaluators(evalRes.data?.evaluators || []);
      setOrgMembers(evalRes.data?.orgMembers || []);
      toast.success('Evaluators assigned!');
    } catch (err) {
      toast.error('Failed to assign evaluators');
    } finally {
      setSavingEvaluators(false);
    }
  };

  const handleRemoveEvaluator = async (userId) => {
    try {
      await axios.delete(`/api/tryout-scoring/${eventId}/evaluators/${userId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const evalRes = await axios.get(`/api/tryout-scoring/${eventId}/evaluators`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setEvaluators(evalRes.data?.evaluators || []);
      setOrgMembers(evalRes.data?.orgMembers || []);
      toast.success('Evaluator removed');
    } catch (err) {
      toast.error('Failed to remove evaluator');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-start gap-3">
          <div>
            <h2 className="text-xl font-semibold">Tryout Assignment</h2>
            {eventData && (
              <p className="text-sm text-gray-600 mt-1">
                {eventData.organization ? (
                  <>League-wide tryout for <strong>{eventData.organization.name}</strong></>
                ) : eventData.team ? (
                  <>Team tryout: <strong>{eventData.team.name}</strong></>
                ) : null}
              </p>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2 flex-shrink-0">
            {(userIsEvaluator || userIsManager) && (
              <a
                href={`/tryout-scoring?eventId=${eventId}`}
                className="btn-primary text-sm flex items-center gap-1.5"
              >
                <StarIcon className="h-4 w-4" />
                Score Players
              </a>
            )}
            <label className="text-sm text-gray-600">Age Group</label>
          <select
            value={selectedAgeGroupKey}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedAgeGroupKey(value);
              if (value === '') {
                setSelectedAgeGroupLabel('');
              } else if (value.startsWith('id:')) {
                const id = parseInt(value.slice(3), 10);
                const found = ageGroups.find(ag => ag.id === id);
                setSelectedAgeGroupLabel(found?.name || '');
              } else if (value.startsWith('name:')) {
                setSelectedAgeGroupLabel(value.slice(5));
              } else {
                setSelectedAgeGroupLabel('');
              }
            }}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="">All</option>
            {ageGroups.map(ag => (
              <option
                key={ag.id ? `id:${ag.id}` : `name:${ag.name}`}
                value={ag.id ? `id:${ag.id}` : `name:${ag.name}`}
              >
                {ag.name}{ag.source === 'configured' ? (ag.organization_id ? ' (Org)' : ' (Global)') : ' (Team)'}
              </option>
            ))}
          </select>
        </div>
        </div>

        {/* Tab bar */}
        <div className="mt-4 flex gap-1 border-t border-gray-100 pt-3">
          {[
            { key: 'assignment', label: 'Assignment', Icon: ClipboardDocumentListIcon },
            ...(userIsManager ? [{ key: 'evaluators', label: `Evaluators (${evaluators.length})`, Icon: UserGroupIcon }] : []),
            { key: 'scores', label: 'Rankings', Icon: StarIcon },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === key
                  ? 'text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              style={activeTab === key ? { backgroundColor: 'var(--theme-primary)' } : {}}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'assignment' && (
        <div className={`rounded-lg border p-4 ${
          draftStatus === 'finalized'
            ? 'border-green-200 bg-green-50'
            : 'border-amber-200 bg-amber-50'
        }`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              {draftStatus === 'finalized' ? (
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
              ) : (
                <ArrowsRightLeftIcon className="h-6 w-6 text-amber-600 flex-shrink-0" />
              )}
              <div>
                <h3 className="font-semibold text-gray-900">
                  {draftStatus === 'finalized' ? 'Rosters Finalized' : 'Draft Mode'}
                </h3>
                <p className="text-sm text-gray-600">
                  {draftStatus === 'finalized'
                    ? `Team memberships are active${draftFinalizedAt ? ` as of ${new Date(draftFinalizedAt).toLocaleString()}` : ''}.`
                    : 'Build rosters here without notifying families. Drag drafted players between teams to conduct trades.'}
                </p>
              </div>
            </div>
            {draftStatus !== 'finalized' && canFinalizeDraft && (
              <button
                onClick={handleFinalizeDraft}
                disabled={finalizingDraft || draftSaving || registrants.length === 0}
                className="btn-primary text-sm flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                <CheckCircleIcon className="h-4 w-4" />
                {finalizingDraft ? 'Finalizing…' : 'Finalize Rosters'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── EVALUATORS TAB ───────────────────────────────────── */}
      {activeTab === 'evaluators' && userIsManager && (
        <div className="bg-white rounded-lg shadow p-4 space-y-4">
          <div>
            <h3 className="font-medium text-gray-900">Assigned Evaluators</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              These users can score players from the app. Scoring is blind — evaluators only see their own scores.
            </p>
          </div>

          {evaluators.length === 0 ? (
            <p className="text-sm text-gray-400">No evaluators assigned yet.</p>
          ) : (
            <div className="space-y-1.5">
              {evaluators.map((ev) => (
                <div key={ev.userId} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    {(ev.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{ev.name}</p>
                    <p className="text-xs text-gray-500 truncate">{ev.email}</p>
                  </div>
                  <button
                    onClick={() => handleRemoveEvaluator(ev.userId)}
                    className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    title="Remove evaluator"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {orgMembers.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Add Evaluators from Org</h4>
              <div className="max-h-48 overflow-y-auto space-y-1 border border-gray-200 rounded-lg p-2">
                {orgMembers.map((member) => (
                  <label
                    key={member.id}
                    className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedEvaluatorIds.has(member.id)}
                      onChange={(e) => {
                        setSelectedEvaluatorIds((prev) => {
                          const next = new Set(prev);
                          e.target.checked ? next.add(member.id) : next.delete(member.id);
                          return next;
                        });
                      }}
                      className="rounded border-gray-300"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>
                      <p className="text-xs text-gray-500 truncate">{member.email}</p>
                    </div>
                  </label>
                ))}
              </div>
              <button
                onClick={handleAddEvaluators}
                disabled={selectedEvaluatorIds.size === 0 || savingEvaluators}
                className="btn-primary mt-2 text-sm flex items-center gap-1.5 disabled:opacity-40"
              >
                <UserGroupIcon className="h-4 w-4" />
                {savingEvaluators ? 'Adding…' : `Add ${selectedEvaluatorIds.size > 0 ? selectedEvaluatorIds.size : ''} Selected`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── RANKINGS TAB ─────────────────────────────────────── */}
      {activeTab === 'scores' && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">Player Rankings</h3>
            <span className="text-xs text-gray-400">Sorted by average overall score · Higher is better</span>
          </div>
          {sortedRegistrants.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No registrants yet.</p>
          ) : (
            <div className="space-y-1.5">
              {sortedRegistrants.map((r, idx) => {
                const displayName = r.player?.name || r.user?.name || r.playerName || 'Unknown';
                const agg = aggregated[r.registrantId];
                const hasScore = agg && agg.overall != null;
                const CATS = [
                  { key: 'effort', label: 'Effort' },
                  { key: 'hitting', label: 'Hitting' },
                  { key: 'pitching', label: 'Pitching' },
                  { key: 'running', label: 'Running' },
                  { key: 'fielding_infield', label: 'IF' },
                  { key: 'fielding_outfield', label: 'OF' },
                ];
                return (
                  <div key={r.registrantId} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-100 hover:bg-gray-50">
                    <span className={`text-sm font-bold w-6 text-center flex-shrink-0 ${hasScore ? 'text-gray-700' : 'text-gray-300'}`}>
                      {hasScore ? idx + 1 : '–'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                      {hasScore && (
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          {CATS.map(({ key, label }) => {
                            const val = agg.categories?.[key];
                            return val != null ? (
                              <span key={key} className="text-[11px] text-gray-500">
                                {label}: <strong className="text-gray-700">{val.toFixed(1)}</strong>
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-right">
                      {hasScore ? (
                        <div>
                          <span className="text-lg font-bold text-gray-900">{agg.overall.toFixed(1)}</span>
                          <span className="text-xs text-gray-400 ml-1">/ 10</span>
                          <p className="text-[11px] text-gray-400">{agg.evaluatorCount} evaluator{agg.evaluatorCount !== 1 ? 's' : ''}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Not scored</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'assignment' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">Available Players</h3>
            <span className="text-xs text-gray-500">
              {filteredRegistrants.length} shown
              {selectedAgeGroupKey ? ` of ${registrants.length}` : ''}
            </span>
          </div>
          <div className="space-y-2 min-h-[240px]">
            {loading && <div className="text-sm text-gray-500">Loading…</div>}
            {!loading && filteredRegistrants.length === 0 && (
              <div className="text-sm text-gray-500">
                {selectedAgeGroupKey ? 'No registrants match this age group.' : 'No registrants yet.'}
              </div>
            )}
            {filteredRegistrants.map((r, idx) => {
              const userId = r.user?.id || r.player?.id || `guest_${r.invoiceId}`;
              const displayName = r.player?.name || r.user?.name || 'Unknown';
              const displayEmail = r.player?.email || r.user?.email || r.parent?.email || '';
              const isGuest = r.isGuest || !r.user?.id;
              const dob = r.player?.date_of_birth || r.user?.date_of_birth || null;
              const assignedAge = r.assignedAgeGroup?.name;
              const numberDraft = editingNumber[r.registrantId];
              const isEditingNum = numberDraft !== undefined;

              return (
                <div
                  key={userId || idx}
                  draggable={draftStatus !== 'finalized'}
                  onDragStart={(e) => onDragStart(e, r)}
                  className={`p-2 border rounded hover:bg-gray-50 ${draftStatus === 'finalized' ? 'cursor-default' : 'cursor-grab'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-sm font-medium flex items-center gap-2">
                        {displayName}
                        {isGuest && (
                          <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                            Guest
                          </span>
                        )}
                        <ScoreBadge registrantId={r.registrantId} />
                      </div>
                      <div className="text-xs text-gray-500">
                        {displayEmail}
                        {r.parent && r.parent.name !== displayName && (
                          <span className="ml-2">(Parent: {r.parent.name})</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                        {dob && <span>DOB: {new Date(dob).toLocaleDateString()}</span>}
                        {assignedAge && (
                          <span className="text-gray-500">
                            Age Group: <strong>{assignedAge}</strong>
                          </span>
                        )}
                      </div>
                      {draftStatus !== 'finalized' && canFinalizeDraft && (
                        <button
                          type="button"
                          onClick={() => saveDraftPlacement(r, null, true)}
                          disabled={draftSaving}
                          className="mt-1.5 inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900"
                        >
                          <SparklesIcon className="h-3.5 w-3.5" />
                          Add to hat picks
                        </button>
                      )}
                      {draftStatus !== 'finalized' && teams.length > 0 && (
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value) saveDraftPlacement(r, Number(e.target.value), false);
                          }}
                          disabled={draftSaving}
                          className="ml-2 mt-1.5 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-600"
                          aria-label={`Draft ${displayName} to a team`}
                        >
                          <option value="">Draft to…</option>
                          {teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
                        </select>
                      )}
                      {/* Tryout number editor */}
                      <div className="flex items-center gap-1.5 mt-1.5" onClick={(e) => e.stopPropagation()}>
                        {isEditingNum ? (
                          <>
                            <input
                              type="number"
                              min="1"
                              value={numberDraft}
                              onChange={(e) =>
                                setEditingNumber((prev) => ({ ...prev, [r.registrantId]: e.target.value }))
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveTryoutNumber(r.registrantId, numberDraft);
                                if (e.key === 'Escape') setEditingNumber((prev) => { const n = { ...prev }; delete n[r.registrantId]; return n; });
                              }}
                              autoFocus
                              className="w-16 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[var(--theme-primary)]"
                              placeholder="#"
                            />
                            <button
                              className="text-xs text-white px-2 py-0.5 rounded"
                              style={{ backgroundColor: 'var(--theme-primary)' }}
                              onClick={() => handleSaveTryoutNumber(r.registrantId, numberDraft)}
                            >
                              Save
                            </button>
                            <button
                              className="text-xs text-gray-500 hover:text-gray-700"
                              onClick={() => setEditingNumber((prev) => { const n = { ...prev }; delete n[r.registrantId]; return n; })}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            className="text-xs text-gray-400 hover:text-gray-700 border border-dashed border-gray-300 px-1.5 py-0.5 rounded"
                            onClick={() =>
                              setEditingNumber((prev) => ({
                                ...prev,
                                [r.registrantId]: r.tryoutNumber != null ? String(r.tryoutNumber) : ''
                              }))
                            }
                          >
                            {r.tryoutNumber != null ? `#${r.tryoutNumber}` : '+ Add #'}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 whitespace-nowrap">
                      ${Number(r.amount || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {draftStatus !== 'finalized' && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div>
                  <h3 className="font-medium flex items-center gap-1.5">
                    <SparklesIcon className="h-4 w-4 text-amber-600" />
                    Hat Picks
                  </h3>
                  <p className="text-xs text-gray-500">Players who did not try out</p>
                </div>
                {canFinalizeDraft && (
                  <button
                    type="button"
                    onClick={handleRandomizeHatPicks}
                    disabled={!hatPickRegistrants.length || !teams.length || randomizingHatPicks}
                    className="text-xs font-medium rounded-md bg-amber-600 px-2.5 py-1.5 text-white hover:bg-amber-700 disabled:opacity-40"
                  >
                    {randomizingHatPicks ? 'Randomizing…' : 'Randomize'}
                  </button>
                )}
              </div>
              <div className="space-y-1.5">
                {hatPickRegistrants.length === 0 ? (
                  <p className="text-xs text-gray-400 rounded border border-dashed border-gray-200 p-3 text-center">
                    Add players here, then randomize them across the visible teams.
                  </p>
                ) : hatPickRegistrants.map(registrant => (
                  <div key={registrant.registrantId || registrant.invoiceId} className="flex items-center gap-2 rounded bg-amber-50 px-2 py-1.5 text-sm text-amber-900">
                    <span className="min-w-0 flex-1 truncate">
                      {registrant.player?.name || registrant.user?.name || 'Unknown'}
                    </span>
                    {canFinalizeDraft && (
                      <button
                        type="button"
                        onClick={() => saveDraftPlacement(registrant, null, false)}
                        className="text-xs text-amber-700 hover:text-amber-950"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">
              Teams {selectedAgeGroupLabel && `(${selectedAgeGroupLabel})`}
            </h3>
            {eventData?.organization && (
              <span className="text-sm font-normal text-gray-600">
                {eventData.organization.name}
              </span>
            )}
          </div>
          {teams.length === 0 ? (
            <div className="text-sm text-gray-500 py-8 text-center">
              {selectedAgeGroupLabel
                ? `No teams found for age group: ${selectedAgeGroupLabel}`
                : 'No teams available. Select an age group to filter.'}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {teams.map(team => {
                const rosterPlayers = teamAssignments[team.id] || [];
                const draftPlayers = registrants.filter(registrant => Number(registrant.draftTeamId) === Number(team.id));
                const rosterCount = rosterPlayers.length + (draftStatus === 'finalized' ? 0 : draftPlayers.length);
                return (
                  <div
                    key={team.id}
                    onDrop={draftStatus === 'finalized' ? undefined : (e) => onDropTeam(e, team.id)}
                    onDragOver={onDragOver}
                    className="border rounded-lg p-3 min-h-[190px] hover:border-blue-400"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium text-sm">{team.name}</div>
                        <div className="text-xs text-gray-500">
                          {team.ageGroup || 'Unassigned'} • {team.season || 'Season TBA'}
                        </div>
                      </div>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        {rosterCount}
                      </span>
                    </div>

                    {rosterPlayers.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Current roster</div>
                        {rosterPlayers.map((player, idx) => (
                          <div key={player.userId || idx} className="text-xs bg-gray-50 text-gray-700 px-2 py-1 rounded flex items-center justify-between">
                            <span>{player.name}</span>
                            {player.isGuest && (
                              <span className="text-[10px] bg-gray-200 text-gray-700 px-1 rounded ml-1">Guest</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {draftStatus !== 'finalized' && draftPlayers.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <div className="text-[11px] font-medium uppercase tracking-wide text-blue-600">Draft picks</div>
                        {draftPlayers.map(registrant => (
                          <div
                            key={registrant.registrantId || registrant.invoiceId}
                            draggable
                            onDragStart={(e) => onDragStart(e, registrant)}
                            className="group flex cursor-grab items-center gap-1.5 rounded bg-blue-50 px-2 py-1.5 text-xs text-blue-800"
                          >
                            <span className="min-w-0 flex-1 truncate">
                              {registrant.player?.name || registrant.user?.name || 'Unknown'}
                            </span>
                            {registrant.isHatPick && (
                              <span title="Hat pick"><SparklesIcon className="h-3.5 w-3.5 text-amber-600" /></span>
                            )}
                            <select
                              value={team.id}
                              onChange={(e) => saveDraftPlacement(registrant, Number(e.target.value), registrant.isHatPick)}
                              disabled={draftSaving}
                              className="max-w-[82px] rounded border border-blue-200 bg-white px-1 py-0.5 text-[10px] text-blue-700"
                              aria-label={`Trade ${registrant.player?.name || registrant.user?.name || 'player'} to another team`}
                            >
                              {teams.map(targetTeam => (
                                <option key={targetTeam.id} value={targetTeam.id}>{targetTeam.name}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => saveDraftPlacement(registrant, null, registrant.isHatPick)}
                              className="text-blue-500 hover:text-red-600"
                              title={registrant.isHatPick ? 'Return to hat-pick pool' : 'Return to available players'}
                            >
                              <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {rosterPlayers.length === 0 && draftPlayers.length === 0 && (
                      <div className="text-xs text-gray-400 mt-2">
                        {draftStatus === 'finalized' ? 'No players on this roster' : 'Drop players here to draft'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>}
    </div>
  );
};

export default TryoutAssignment;
